// Lib
import React from 'react';

// Utils
import { getChildren } from '../../../../common/elements/utils/elementTraversalUtils';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';
import { clearOnboardingProgressStep, setOnboardingProgressStep } from './onboardingProgressService';
import { selectMostRecentBoard } from './onboardingStepsUtils';
import platformSingleton from '../../../platform/platformSingleton';

// Selectors
import { getElements } from '../../../element/selectors/elementsSelector';
import { getCurrentUser, getCurrentUserRootBoardId } from '../../../user/currentUserSelector';
import { getUserEducation } from '../../../../common/users/utils/userPropertyUtils';
import { hasSeen } from '../../../../common/users/userEducationUtil';
import { areAliasesInitialised, isCurrentUserInitialised } from '../../../app/initialisation/initialisationSelector';
import {
    getCmsTemplatesFetched,
    getOnboardingBoardTitle,
    getOnboardingTemplateBoardId,
} from '../../boardTemplatePickerPopup/boardTemplatePickerSelectors';
import { getCurrentBoardId } from '../../../reducers/currentBoardId/currentBoardIdSelector';
import { selectIsSocketConnected } from '../../../utils/socket/socketConnectionSelector';

// Actions
import { getColor } from '../../../element/board/boardActions';
import { createElementSync } from '../../../element/actions/elementActions';
import { navigateToBoard } from '../../../reducers/navigationActions';
import {
    clearPersistedUserOnboarding,
    markEducationCodeAsSeen,
    onboardingNavigateToSegmentPicker,
    onboardingSetMetadata,
} from '../onboardingActions';
import { CurrentToolbarActionType } from '../../toolbar/currentToolbarActions';

// Components
import OnboardingTooltipCloseButton from '../tooltips/OnboardingTooltipCloseButton';
import OnboardingTooltipContentSuggestBoardNames from '../tooltips/OnboardingTooltipContentSuggestBoardNames';

// Constants
import { ONBOARDING_EDUCATION_CODES } from '../../../../common/users/userEducationConstants';
import {
    ELEMENTS_DESELECTED,
    ELEMENTS_DESELECT_ALL,
    ELEMENTS_SELECTED,
} from '../../../../common/elements/selectionConstants';
import { DRAG_START } from '../../../reducers/draggingConstants';
import { ONBOARDING_STEP_PASSED } from '../onboardingConstants';
import { BoardSections } from '../../../../common/boards/boardConstants';
import { ElementType } from '../../../../common/elements/elementTypes';
import { OnboardingFlow } from './onboardingConfigTypes';
import { TooltipPositions } from '../../../components/tooltips/tooltipConstants';
import { ToolbarListType } from '../../toolbar/toolbarTypes';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { SheetActionTypes } from '../../../mobile/structural/sheet/sheetActionTypes';
import { ELEMENT_CREATE, ELEMENT_UPDATE } from '../../../../common/elements/elementConstants';
import { CURRENT_BOARD_ID_SET } from '../../../reducers/currentBoardId/currentBoardIdConstants';
import { OnboardingStepsAddTemplate } from './onboardingStepsAddTemplate';

export enum OnboardingStepsAddTemplateMobile {
    MAIN = 'mm-use-template',
    CREATE_BOARD_0 = 'mm-use-template-create-board-0',
    CREATE_BOARD_1 = 'mm-use-template-create-board-1',
    CREATE_BOARD_1A_DRAGGING_BOARD = 'mn-use-template-create-board-1a-dragging-board',
    CREATE_BOARD_1B_NAME_BOARD = 'mn-use-template-create-board-1b-name-board',
    CREATE_BOARD_1C_NAVIGATE_TO_BOARD = 'mn-use-template-create-board-1c-navigate-to-board',

    NEW_USER_0_PREPARE = 'mm-use-template-new-user-0-prepare',
    NEW_USER_1_SEGMENT_PICKER = 'mm-use-template-new-user-1-segment-picker',
    NEW_USER_2_INIT_BOARD = 'mm-use-template-new-user-2-init-board',
    NEW_USER_3_WAIT_FOR_USE = 'mm-use-template-new-user-3-wait-for-use',
    NEW_USER_4_EXPLAIN_TOOLBAR = 'mm-use-template-new-user-4-explain-toolbar',
    NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN = 'mm-use-template-new-user-4a-explain-toolbar-hidden',
}

const getRootBoardHasChildren = (state: any) => {
    const rootBoardId = getCurrentUserRootBoardId(state);
    const elements = getElements(state);
    const rootBoardChildrenIds = getChildren(elements, rootBoardId);
    return rootBoardChildrenIds.size > 0;
};

const MM_ADD_TEMPLATE_STEPS_DRAG_OUT_BOARD: OnboardingFlow = {
    [OnboardingStepsAddTemplateMobile.CREATE_BOARD_0]: {
        id: OnboardingStepsAddTemplateMobile.CREATE_BOARD_0,
        callback: () => {
            clearOnboardingProgressStep();
        },
        nextStep: (action) => {
            if (action.type === CurrentToolbarActionType.TOOLBAR_STATE_UPDATE) {
                const { toolbarState } = action;
                const { toolbars, focusIndex } = toolbarState;

                const currentToolbarType = toolbars[focusIndex]?.type;
                if (currentToolbarType !== ToolbarListType.MOBILE_TABS)
                    return OnboardingStepsAddTemplateMobile.CREATE_BOARD_1;
            }
        },
        tip: {
            selector: '#create-element-popup-selector',
            position: TooltipPositions.TOP,
            distance: 10,
            component: () => <div className="primary-text">Tap + to get started</div>,
            style: {
                width: '25rem',
            },
        },
    },
    [OnboardingStepsAddTemplateMobile.CREATE_BOARD_1]: {
        id: OnboardingStepsAddTemplateMobile.CREATE_BOARD_1,
        callback: () => {
            clearOnboardingProgressStep();
        },
        nextStep: (action) => {
            if (action.type === CurrentToolbarActionType.TOOLBAR_STATE_UPDATE) {
                const { toolbarState } = action;
                const { toolbars, focusIndex } = toolbarState;

                const currentToolbarType = toolbars[focusIndex]?.type;
                if (currentToolbarType === ToolbarListType.MOBILE_TABS)
                    return OnboardingStepsAddTemplateMobile.CREATE_BOARD_0;
            }
            if (action.type === DRAG_START) return OnboardingStepsAddTemplateMobile.CREATE_BOARD_1A_DRAGGING_BOARD;
        },
        tip: {
            selector: '#element-tool-board',
            position: TooltipPositions.TOP,
            delay: 300,
            distance: 10,
            component: () => <div className="primary-text">Drag out a board to create your template</div>,
            style: {
                width: '25rem',
            },
        },
    },
    [OnboardingStepsAddTemplateMobile.CREATE_BOARD_1A_DRAGGING_BOARD]: {
        id: OnboardingStepsAddTemplateMobile.CREATE_BOARD_1A_DRAGGING_BOARD,
        nextStep: (action) => {
            if (action.type === ELEMENT_CREATE) return OnboardingStepsAddTemplateMobile.CREATE_BOARD_1B_NAME_BOARD;
        },
    },
    [OnboardingStepsAddTemplateMobile.CREATE_BOARD_1B_NAME_BOARD]: {
        id: OnboardingStepsAddTemplateMobile.CREATE_BOARD_1B_NAME_BOARD,
        nextStep: (action) => {
            switch (action.type) {
                case ELEMENT_UPDATE:
                    return OnboardingStepsAddTemplateMobile.CREATE_BOARD_1C_NAVIGATE_TO_BOARD;
                default:
                    return;
            }
        },
        tip: {
            selector: selectMostRecentBoard,
            position: TooltipPositions.BOTTOM,
            distance: 15,
            component: () => (
                <div>
                    <OnboardingTooltipCloseButton />
                    <div className="primary-text">Let’s give your new board a name</div>
                    <OnboardingTooltipContentSuggestBoardNames />
                </div>
            ),
            style: {
                width: '30rem',
            },
        },
    },
    [OnboardingStepsAddTemplateMobile.CREATE_BOARD_1C_NAVIGATE_TO_BOARD]: {
        id: OnboardingStepsAddTemplateMobile.CREATE_BOARD_1C_NAVIGATE_TO_BOARD,
        nextStep: (action) => {
            switch (action.type) {
                case CURRENT_BOARD_ID_SET:
                    return OnboardingStepsAddTemplate.CREATE_BOARD_2_WAIT_FOR_USE;
                default:
                    return;
            }
        },
        tip: {
            selector: selectMostRecentBoard,
            position: TooltipPositions.BOTTOM,
            distance: 15,
            component: () => (
                <div>
                    <OnboardingTooltipCloseButton />
                    <div className="primary-text">
                        {platformSingleton.features.supportsMouse
                            ? 'Great, now double click the board to open it'
                            : 'Great, now double tap the board to open it'}
                    </div>
                </div>
            ),
        },
    },
};

const MM_ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER: OnboardingFlow = {
    [OnboardingStepsAddTemplateMobile.NEW_USER_0_PREPARE]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_0_PREPARE,
        nextStep: (action, state) => {
            const userInitialisationComplete = areAliasesInitialised(state) && isCurrentUserInitialised(state);

            const socketConnected = selectIsSocketConnected(state);

            if (userInitialisationComplete && socketConnected)
                return OnboardingStepsAddTemplateMobile.NEW_USER_1_SEGMENT_PICKER;
        },
    },
    [OnboardingStepsAddTemplateMobile.NEW_USER_1_SEGMENT_PICKER]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_1_SEGMENT_PICKER,
        dispatch: () => async (dispatch, getState) => {
            dispatch(onboardingNavigateToSegmentPicker());
        },
        callback: () => {
            setOnboardingProgressStep(OnboardingStepsAddTemplateMobile.NEW_USER_1_SEGMENT_PICKER);
        },
        nextStep: (action) => {
            if (action.type === SheetActionTypes.SHEET_CLOSE && action.sheetId === SheetId.SegmentPicker)
                return OnboardingStepsAddTemplateMobile.NEW_USER_2_INIT_BOARD;
        },
    },
    [OnboardingStepsAddTemplateMobile.NEW_USER_2_INIT_BOARD]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_2_INIT_BOARD,
        dispatch: () => async (dispatch, getState) => {
            const state = getState();

            dispatch(
                onboardingSetMetadata({
                    hideTemplatePicker: true,
                }),
            );

            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING));

            // if we get to this point and there _are_ children, on the root board (shouldn't happen),
            // bail from this step
            if (getRootBoardHasChildren(state)) return;

            const rootBoardId = getCurrentUserRootBoardId(state);
            const boardTitle = getOnboardingBoardTitle(state) || 'New Board';

            const color = getColor(boardTitle, state);

            const elementId = dispatch(
                createElementSync({
                    elementType: ElementType.BOARD_TYPE,
                    location: {
                        parentId: rootBoardId,
                        section: BoardSections.CANVAS,
                        position: {
                            x: 10,
                            y: 8,
                        },
                    },
                    content: {
                        title: boardTitle,
                        color,
                    },
                    transactionId: -1,
                    silent: true,
                }),
            );

            dispatch(
                onboardingSetMetadata({
                    templateBoardId: elementId,
                }),
            );

            dispatch(navigateToBoard({ boardId: elementId, replaceUrl: true, trackTiming: false }));
        },
        nextStep: (action, state) => {
            const templatesLoaded = getCmsTemplatesFetched(state);
            const templateBoardId = getOnboardingTemplateBoardId(state);
            const currentBoardId = getCurrentBoardId(state);

            // before proceeding to next step, we need to make sure that...
            if (
                templatesLoaded && // templates are loaded
                templateBoardId === currentBoardId // we have navigated to the new board board
            )
                return OnboardingStepsAddTemplateMobile.NEW_USER_3_WAIT_FOR_USE;
        },
        callback: () => {
            sendAmplitudeEvent({ eventType: 'Started onboarding tutorial' });
        },
    },
    [OnboardingStepsAddTemplateMobile.NEW_USER_3_WAIT_FOR_USE]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_3_WAIT_FOR_USE,
        nextStep: (action) => {
            if (action.type === ONBOARDING_STEP_PASSED && action.step === 'template-picker') {
                return OnboardingStepsAddTemplateMobile.NEW_USER_4_EXPLAIN_TOOLBAR;
            }
        },
        dispatch: () => (dispatch) => {
            dispatch(
                onboardingSetMetadata({
                    hideTemplatePicker: null,
                }),
            );
        },
    },
    [OnboardingStepsAddTemplateMobile.NEW_USER_4_EXPLAIN_TOOLBAR]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_4_EXPLAIN_TOOLBAR,
        nextStep: (action) => {
            if (action.type === CurrentToolbarActionType.TOOLBAR_STATE_UPDATE) {
                const { toolbarState } = action;
                const { toolbars, focusIndex } = toolbarState;

                const currentToolbarType = toolbars[focusIndex]?.type;
                if (currentToolbarType === ToolbarListType.CREATE_ELEMENT) return 'onboarding-finish';
            }

            if (action.type === ELEMENTS_SELECTED)
                return OnboardingStepsAddTemplateMobile.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN;
        },
        dispatch: () => (dispatch) => {
            dispatch(
                // Ensure the auto-selected template only happens once
                onboardingSetMetadata({
                    landing_page: null,
                    select_template: null,
                }),
            );
            dispatch(markEducationCodeAsSeen(ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING));
        },
        callback: () => {
            sendAmplitudeEvent({ eventType: 'Finished onboarding tutorial' });
        },
        tip: {
            selector: '.MobileToolbar #create-element-popup-selector',
            position: TooltipPositions.TOP,
            distance: 10,
            component: () => <div className="primary-text">Tap + to get started</div>,
            delay: 500,
            style: {
                width: '27rem',
            },
        },
    },
    [OnboardingStepsAddTemplateMobile.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN]: {
        id: OnboardingStepsAddTemplateMobile.NEW_USER_4A_EXPLAIN_TOOLBAR_HIDDEN,
        nextStep: (action) => {
            switch (action.type) {
                case ELEMENTS_DESELECTED:
                case ELEMENTS_DESELECT_ALL:
                    return OnboardingStepsAddTemplateMobile.NEW_USER_4_EXPLAIN_TOOLBAR;
                default:
                    return;
            }
        },
    },
};

const ADD_TEMPLATE_STEPS_MOBILE: OnboardingFlow = {
    // Config for sub-flows
    ...MM_ADD_TEMPLATE_STEPS_DRAG_OUT_BOARD,
    ...MM_ADD_TEMPLATE_STEPS_FLOW_CONFIG_NEW_USER,

    // Entry point for template onboarding
    [OnboardingStepsAddTemplateMobile.MAIN]: {
        id: OnboardingStepsAddTemplateMobile.MAIN,
        nextStep: (_, state) => {
            const currentUser = getCurrentUser(state);
            const education = getUserEducation(currentUser);

            const isExistingUser = hasSeen(education, ONBOARDING_EDUCATION_CODES.DESKTOP_ONBOARDING);
            if (isExistingUser) return OnboardingStepsAddTemplateMobile.CREATE_BOARD_0;

            return OnboardingStepsAddTemplateMobile.NEW_USER_0_PREPARE;
        },
        dispatch: () => (dispatch) => {
            dispatch(clearPersistedUserOnboarding());
        },
        callback: () => {
            clearOnboardingProgressStep();
        },
    },
};

export default ADD_TEMPLATE_STEPS_MOBILE;
